import React from 'react';
import Spinner from './Spinner';

const ICON_ONLY_SIZES = new Set(['icon', 'icon-sm']);

/**
 * Button — the design-system button primitive.
 *
 * Props:
 *   variant: 'primary' | 'secondary' | 'outline' | 'ghost' | 'link' | 'destructive'
 *   size:    'xs' | 'sm' | 'md' | 'lg' | 'icon' | 'icon-sm'
 *   loading: boolean   — shows spinner, sets aria-busy, blocks pointer events (NOT disabled)
 *   leadingIcon / trailingIcon: ReactNode
 *   fullWidth: boolean
 *   as: 'button' | 'a' | React component (e.g. Link)  — render element
 *
 * All native button/anchor props pass through.
 */
const Button = React.forwardRef(function Button(
  {
    variant = 'secondary',
    size = 'md',
    loading = false,
    leadingIcon,
    trailingIcon,
    fullWidth = false,
    as: Component = 'button',
    className = '',
    children,
    disabled,
    type,
    ...rest
  },
  ref,
) {
  const classes = [
    'ps-btn',
    `ps-btn--${variant}`,
    `ps-btn--${size}`,
    fullWidth ? 'ps-btn--full' : '',
    loading ? 'is-loading' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  const isIconOnly = ICON_ONLY_SIZES.has(size);
  const spinnerSize = size === 'xs' || size === 'icon-sm' ? 14 : 16;

  // Only set a default type on real <button> elements.
  const resolvedType = Component === 'button' ? type || 'button' : type;

  return (
    <Component
      ref={ref}
      className={classes}
      type={resolvedType}
      disabled={Component === 'button' ? disabled : undefined}
      aria-disabled={Component !== 'button' && disabled ? true : undefined}
      aria-busy={loading || undefined}
      {...rest}
    >
      {loading ? (
        <span className="ps-btn__icon"><Spinner size={spinnerSize} /></span>
      ) : (
        leadingIcon && <span className="ps-btn__icon">{leadingIcon}</span>
      )}
      {!isIconOnly && children != null && <span className="ps-btn__label">{children}</span>}
      {isIconOnly && !loading && children}
      {!loading && trailingIcon && <span className="ps-btn__icon">{trailingIcon}</span>}
    </Component>
  );
});

/**
 * IconButton — icon-only button. Requires `aria-label`.
 */
export const IconButton = React.forwardRef(function IconButton(
  { size = 'icon', 'aria-label': ariaLabel, children, ...rest },
  ref,
) {
  if (process.env.NODE_ENV !== 'production' && !ariaLabel) {
    // eslint-disable-next-line no-console
    console.warn('IconButton requires an aria-label for accessibility.');
  }
  return (
    <Button ref={ref} size={size} aria-label={ariaLabel} {...rest}>
      {children}
    </Button>
  );
});

export default Button;
